import { type Vec2, cellCentre } from '@d2/core';

/**
 * The isometric projection — the whole of the renderer's cell↔pixel boundary.
 *
 * `packages/core` thinks in cells and has no idea what a pixel is; that is the
 * point of it. Every conversion between the two therefore lives here, in one
 * pure module with no Phaser import, so it can be unit-tested without a browser
 * and reused by anything that needs it: placing a sprite, centring the camera,
 * and turning a click back into the cell the player meant.
 *
 * Three conventions, all deliberate:
 *
 *  - **Positions are continuous grid coordinates**, matching core. Cell `(3, 4)`
 *    covers `[3, 4) × [4, 5)`, so the middle of it is `(3.5, 4.5)`. That is what
 *    makes `screenToCell` a plain `floor` and what lets an entity walking
 *    between two cells project correctly rather than snapping.
 *  - **The half-cell offset is core's**, not the renderer's (TODO rows 60, 80).
 *    `cellCentre` is imported rather than defined here: the middle of a cell is
 *    where an entity stands *and* where its route's waypoints sit, and a second
 *    copy of that `+0.5` would let prediction and truth drift by half a cell.
 *  - **`gridToScreen` maps the grid origin to the screen origin.** No half-tile
 *    fudge is baked in; the tilemap layer is offset instead (see
 *    `dungeon.scene.ts`), because a constant hidden in a projection is a
 *    constant that gets rediscovered painfully by the next caller.
 */

export interface IsoTileSize {
  /** Full width of a tile's diamond, in pixels. */
  readonly width: number;
  /** Full height of a tile's diamond, in pixels. Half the width, for a 2:1 projection. */
  readonly height: number;
}

/**
 * Placeholder tile metrics: a 64×32 diamond, the classic 2:1 isometric ratio.
 * Real art lands post-M8 (TODO row 9) and will bring its own numbers; nothing
 * outside this module should hard-code either figure.
 */
export const ISO_TILE: IsoTileSize = { width: 64, height: 32 };

/** Screen position of a continuous grid position. */
export const gridToScreen = (position: Vec2, tile: IsoTileSize = ISO_TILE): Vec2 => ({
  x: (position.x - position.y) * (tile.width / 2),
  y: (position.x + position.y) * (tile.height / 2),
});

/**
 * The exact inverse of `gridToScreen`.
 *
 * Derived rather than approximated: with `sx = (x - y)·w/2` and
 * `sy = (x + y)·h/2`, adding and subtracting the two gives `x` and `y` back in
 * one step each. Click-to-move will lean on this being an inverse and not a
 * near-inverse.
 */
export const screenToGrid = (point: Vec2, tile: IsoTileSize = ISO_TILE): Vec2 => {
  const halfWidths = point.x / tile.width;
  const halfHeights = point.y / tile.height;

  return { x: halfWidths + halfHeights, y: halfHeights - halfWidths };
};

/** Screen position of the middle of a cell's diamond. */
export const cellToScreen = (cell: Vec2, tile: IsoTileSize = ISO_TILE): Vec2 => gridToScreen(cellCentre(cell), tile);

/** The cell containing a screen point. The inverse of `cellToScreen`, and what a click resolves to. */
export const screenToCell = (point: Vec2, tile: IsoTileSize = ISO_TILE): Vec2 => {
  const position = screenToGrid(point, tile);

  return { x: Math.floor(position.x), y: Math.floor(position.y) };
};

/**
 * How near the camera something is: larger means drawn later, and so on top.
 *
 * It is `x + y` because that is exactly the screen `y` of a position, scaled —
 * so ordering by depth *is* ordering by how far down the screen a thing sits,
 * which is the whole of the painter's algorithm in isometric space.
 */
export const isoDepth = (position: Vec2): number => position.x + position.y;

/**
 * The depth a whole band of rock — a diagonal of constant `x + y` — is drawn at.
 *
 * The parameter is the band *number* — the `x + y` every cell of the band
 * shares — and not a position in any array. A tile's depth is measured at the
 * middle of its diamond, half a cell forward on each axis, which comes out one
 * whole band ahead of the number the band is named by; that is the `+ 1`.
 *
 * One depth for the entire band is safe, and that is the licence for drawing a
 * band as a single display object: the cells of a band sit side by side on one
 * screen line, a full tile apart, so no two of them overlap and any order
 * within the band paints the same picture. Entities are then sorted into the
 * stack of bands one by one, by their own `isoDepth`, which is what makes the
 * count of layers a property of the *map* and not of how many things are
 * standing on it.
 */
export const bandDepth = (band: number): number => band + 1;

/**
 * How far the way down is lifted out of the depth order, in bands. **Three.**
 *
 * A rock tile is drawn a whole `ROCK_RISE` above its own diamond, which is what
 * makes it read as a wall — and what makes it cover the cells *behind* it. Three
 * tiles can reach back over a given cell that way: the two beside it at
 * `x + y + 1`, and the one diagonally in front at `x + y + 2`, whose raised top
 * face lands exactly on top of it. `bandDepth` puts those at `x + y + 3` at the
 * deepest, so a mark drawn at the cell's own `x + y + 1` is buried by any of
 * them.
 *
 * For a monster or a drop that is correct and is the whole point of the depth
 * sort: things behind walls are behind walls. **For the exit it is fatal.** The
 * stairs are placed at maximum flow distance from the entrance, which is
 * overwhelmingly a dead end, and a dead end is exactly the shape that has rock
 * diagonally in front of it — on three of the first eight seeds tried, the way
 * down was hidden from every camera angle this projection has. A floor whose
 * exit cannot be seen is a floor that cannot be finished, and no amount of
 * pointing at it from the edge of the screen fixes the last two steps.
 *
 * So the exit is lifted clear of the rock it is set into, and the cost is
 * stated rather than hidden: **the well shows through the wall immediately in
 * front of it**, which at placeholder fidelity reads as light spilling out of an
 * alcove, and it is drawn over the feet of a body standing on the cell. Neither
 * is free and both are cheaper than an unfinishable floor.
 *
 * What is deliberately *not* lifted is the **click**. `targeting.ts` hit-tests
 * the stairs at the cell's own depth and below every body and drop, so a monster
 * standing on the exit is still the thing a click on it names.
 */
export const STAIRS_DEPTH_LIFT = 3;

/**
 * The depth the way down is drawn at — its own cell's, lifted clear of the rock
 * that would otherwise bury it.
 */
export const stairsDepth = (cell: Vec2): number => isoDepth(cellCentre(cell)) + STAIRS_DEPTH_LIFT;

/**
 * `true` when the tile at `cell` is nearer the camera than something standing at
 * `position`, and so may cover it.
 *
 * This one predicate is the depth-sorting rule, and it is now literally the
 * comparison the renderer makes: a band of rock carries `bandDepth`, an entity
 * carries `isoDepth`, and whichever is larger is painted last. A tie — an
 * entity in the same band as a tile — is a genuine draw, since the two stand on
 * the same screen line and cannot overlap.
 */
export const occludes = (cell: Vec2, position: Vec2): boolean => bandDepth(isoDepth(cell)) > isoDepth(position);
